import { z } from 'zod';

/**
 * **A vida, em MILLI: a unidade exata, o codec do fio e o formatador.**
 *
 * ## Porque milli (gate V28G.S.1)
 *
 * Porque nenhuma fonte legal de vida máxima produz um ponto inteiro. A auditoria
 * do V28G.S mediu-o no catálogo do equipamento: `flatMaxHealth` é sorteado por
 * MILLI sobre `[2 000, 12 000]` - dez mil e um valores, onze deles múltiplos de
 * mil - e `maxHealthPercent` por BPS sobre `[150, 900]`. Uma Câmara comum vale
 * 102,001 ou 102,999 de vida, e um Mother Ship nível 1 sozinho vale 108,12.
 *
 * O duelo media vida em pontos inteiros e truncava até 0,999 do que a caça
 * tinha. Passou a medir em milli, e as duas simulações passaram a ter
 * exatamente a mesma vida.
 *
 * ## Porque uma STRING no fio, e não um `number`
 *
 * Porque a progressão não tem tecto. 2^40 pontos alocados valem 1,1 x 10^16
 * milli, acima de `Number.MAX_SAFE_INTEGER` - portanto um `number` de JSON
 * perderia dígitos, e um teto para o evitar seria um teto de progressão escrito
 * num tipo, que é o que o `CLAUDE.md` proíbe. É a mesma decisão que
 * `primordialHeartCount` já tinha tomado, pela mesma razão.
 *
 * O que atravessa é um decimal canónico. O que a simulação usa é um `bigint`.
 */

/**
 * O comprimento de uma vida NO FIO, em dígitos.
 *
 * Um limite de PAYLOAD e não de jogo, e a diferença importa: 40 dígitos é a
 * capacidade de armazenamento de `numeric(40, 0)`, e nenhuma vida real chega
 * perto dela - a maior conta representável vale dezassete dígitos de milli.
 * Existe para que um quadro hostil não entregue ao parser um inteiro de um
 * megabyte, e não para dizer quanta vida alguém pode ter.
 */
export const PVP_HEALTH_MILLI_MAX_DIGITS = 40;

/** 1 000 milli-vida = 1 ponto de vida. */
export const PVP_HEALTH_MILLI_PER_UNIT = 1000n;

/** Decimal canónico, não negativo, sem sinal e sem zeros à esquerda. */
const CANONICAL = /^(0|[1-9][0-9]*)$/;

/**
 * Uma vida lida do fio, ou `null` para tudo o que não é uma.
 *
 * Recusa por FORMA e por comprimento, e nunca por magnitude: não há máximo de
 * jogo aqui. Zero é legal - é um casco destruído, e é o que a instantânea de um
 * destroço guarda.
 */
export function parseHealthMilli(raw: unknown): bigint | null {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > PVP_HEALTH_MILLI_MAX_DIGITS) {
    return null;
  }
  if (!CANONICAL.test(raw)) {
    return null;
  }
  return BigInt(raw);
}

/**
 * O inverso. Uma vida negativa não existe e é recusada em vez de escrita.
 */
export function encodeHealthMilli(value: bigint): string {
  if (value < 0n) {
    throw new RangeError(`health is never negative, got ${value}`);
  }
  return value.toString(10);
}

/**
 * **O formatador canónico de vida, e o único** (gate V28G.S.1).
 *
 * Até três casas decimais, sem zeros finais, sempre com a vírgula decimal do
 * `pt-BR`, e **nunca** em notação científica:
 *
 *     100 000 milli -> `100`
 *     108 120 milli -> `108,12`
 *     102 001 milli -> `102,001`
 *
 * ## Porque ele existe, e porque é `bigint` a entrada
 *
 * Porque uma barra e um número de dano são as duas únicas coisas que um jogador
 * lê, e uma vida convertida para `double` antes de ser impressa perderia
 * dígitos exatamente nas contas grandes - onde `toString()` também trocaria o
 * número por `1.1e+16`, que não é uma vida. A divisão é feita em `bigint` e só
 * os dígitos saem.
 *
 * Três casas e não mais: o milésimo é a menor unidade que existe, portanto
 * nunca há uma quarta para mostrar.
 */
export function formatHealthMilli(value: bigint): string {
  const negative = value < 0n;
  const magnitude = negative ? -value : value;
  const whole = magnitude / PVP_HEALTH_MILLI_PER_UNIT;
  const fraction = magnitude % PVP_HEALTH_MILLI_PER_UNIT;
  const sign = negative ? '-' : '';
  if (fraction === 0n) {
    return `${sign}${whole.toString(10)}`;
  }
  const digits = fraction.toString(10).padStart(3, '0').replace(/0+$/, '');
  return `${sign}${whole.toString(10)},${digits}`;
}

/**
 * O esquema de zod que os dois protocolos usam para uma vida.
 *
 * Um só, e não um por quadro: duas grafias da mesma grandeza seriam duas
 * oportunidades de uma delas aceitar um valor que a outra recusa.
 */
export const pvpHealthMilliSchema = z
  .string()
  .min(1)
  .max(PVP_HEALTH_MILLI_MAX_DIGITS)
  .regex(CANONICAL, 'must be a canonical non-negative decimal');
